import { Seo } from "../components/Seo";
import { Link } from "react-router-dom";
import { Award, Truck, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "../components/PageHero";
import { VG_ORANGE } from "../lib/brand";

const VALUES = [
  { icon: Award, title: "Quality Products", text: "Commercial-grade flooring and matting engineered for durability and performance." },
  { icon: Truck, title: "Single Source", text: "From entrance matting to modular carpet, we are your one-stop flooring outfitter." },
  { icon: Users, title: "Customer First", text: "Responsive service and support for new and existing customers alike." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo title="About Us" description="van Gelder, Inc. is a family-run business with over 48 years of experience in specialty commercial flooring and matting, committed to LEED standards and environmentally friendly standards." />
      <PageHero
        title="About Us"
        subtitle="Your commercial flooring outfitters — solutions for every environment."
        testId="about-hero"
      />
      <section className="mx-auto max-w-4xl px-4 py-14">
        <div className="prose prose-neutral max-w-none space-y-4">
          <h2 className="font-heading text-2xl font-extrabold text-neutral-900">About van Gelder Inc.</h2>
          <p className="text-lg text-neutral-700 leading-relaxed">
            van Gelder Inc. is a family-run business with over 48 years of experience in specialty
            flooring products. Our extensive range includes entrance matting, anti-fatigue flooring,
            logo and commercial mats, modular carpet tiles, the TacFast® Carpet Tile System, grid
            matting systems, and luxury vinyl planks and tiles, just to name a few. These products
            continue to play a vital role in creating safe, clean, and visually appealing commercial
            interiors and entranceways.
          </p>
          <p className="text-neutral-600 leading-relaxed">
            We are committed to continuously improving our offerings to meet and exceed LEED standards and
            environmentally friendly standards. Our dedicated sales, customer service, and support
            teams are well-equipped to handle all your matting and specialty flooring inquiries.
          </p>
          <p className="text-neutral-800 font-semibold leading-relaxed">
            Call us today! We understand that you have choices when it comes to commercial flooring
            and matting. Our team looks forward to the opportunity to collaborate with you on future
            projects.
          </p>

          <h3 className="font-heading text-xl font-bold text-neutral-900 pt-6">Sal &amp; Mariette van Gelder</h3>
          <p className="text-neutral-600 leading-relaxed">
            Sal and Mariette van Gelder, Dutch and French immigrants, founded Charles van Gelder
            Importers, Inc. (AIA member) in 1978. The business began in upstate New York as a
            distributorship for Axminster wool and other flooring products, initially sourced from a
            Quebec-based company that was liquidating inventory. Within four years, the company became
            solely owned and operated by the van Gelder family. Over time, the product offerings
            expanded to include padding, sundries, Magee carpets, and van Gelder's own private-label
            brand—Guildcrest.
          </p>

          <h3 className="font-heading text-xl font-bold text-neutral-900 pt-6">Early 1980s Expansion</h3>
          <p className="text-neutral-600 leading-relaxed">
            In the early 1980s, the company branched out beyond traditional carpeting, leveraging
            international relationships to introduce high-quality, durable fiber-bonded carpet to the
            U.S. market. These carpets were designed to capture up to 80% of dirt, dust, and moisture
            before it could damage interior environments. This innovative product, also known as
            needlepunch carpet, carved out a unique market niche. During this period, regional
            distributors began sourcing entrance matting and carpet tiles directly from van Gelder.
          </p>

          <h3 className="font-heading text-xl font-bold text-neutral-900 pt-6">Strategic Growth</h3>
          <p className="text-neutral-600 leading-relaxed">
            The company adopted a two-fold strategy:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-neutral-600 leading-relaxed">
            <li><strong>International Trade Expansion:</strong> The focus remained on offering premium imported needlepunch carpet and tiles to its Northeast clientele.</li>
            <li><strong>Domestic Geographic Expansion:</strong> A national agent network was developed to reach new customers across the United States.</li>
          </ul>
          <p className="text-neutral-600 leading-relaxed">
            In late 2012, the name "Charles" was officially dropped from the company name, as Charles
            had not been involved in operations for many years. The company rebranded as van Gelder Inc.
          </p>

          <h3 className="font-heading text-xl font-bold text-neutral-900 pt-6">Present Day</h3>
          <p className="text-neutral-600 leading-relaxed">
            Today, van Gelder Inc. blends legacy with innovation. Led by a management team with deep
            experience in international trade, design, and development, we remain committed to offering
            cutting-edge flooring technology. Our product line now includes health-conscious,
            environmentally sustainable options such as:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-neutral-600 leading-relaxed">
            <li>Needlepunch carpet tiles that combat dust mites, bacteria, mold, and fungi</li>
            <li>Flooring solutions designed to reduce triggers for asthma and allergies</li>
            <li>Tiles with recyclable backing</li>
          </ul>
          <p className="text-neutral-600 leading-relaxed">
            We pride ourselves on producing the highest-quality needlepunch flooring in the industry,
            complemented by a growing portfolio of solutions for every environment. Our advanced
            technology and personalized service allow us to offer unmatched flexibility and lead times.
            With a second-generation leadership team at the helm, we continue the legacy established
            over four decades ago—rooted in integrity, guided by innovation, and inspired daily by our
            dedicated employees, customers, and vendors.
          </p>
          <p className="font-heading text-lg font-bold" style={{ color: VG_ORANGE }}>
            Always first class since 1978
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {VALUES.map((v) => {
            const Icon = v.icon;
            return (
              <div key={v.title} className="rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg text-white" style={{ backgroundColor: VG_ORANGE }}>
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-neutral-900">{v.title}</h3>
                <p className="mt-1.5 text-sm text-neutral-600">{v.text}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-12 rounded-xl bg-[#777775] p-8 text-center text-white">
          <h2 className="font-heading text-2xl font-extrabold">Ready to talk flooring?</h2>
          <p className="mt-2 text-neutral-300">Our team is here to help you find the right solution.</p>
          <Button asChild className="mt-5 text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }} data-testid="about-contact-cta">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
